export interface PasswordStrengthResult {
  score: number; // 0–4
  label: "Weak" | "Fair" | "Good" | "Strong";
  missing: string[]; // plain-language list of unmet rules
  isValid: boolean; // true only when all 5 rules pass
}

const RULES = [
  { test: (p: string) => p.length >= 10, label: "at least 10 characters" },
  { test: (p: string) => /[A-Z]/.test(p), label: "uppercase letter" },
  { test: (p: string) => /[a-z]/.test(p), label: "lowercase letter" },
  { test: (p: string) => /[0-9]/.test(p), label: "number" },
  { test: (p: string) => /[^A-Za-z0-9]/.test(p), label: "special character" },
] as const;

const SCORE_LABELS: PasswordStrengthResult["label"][] = ["Weak", "Weak", "Fair", "Good", "Strong"];

export function getPasswordStrength(password: string): PasswordStrengthResult {
  if (!password) {
    return { score: 0, label: "Weak", missing: RULES.map(r => r.label), isValid: false };
  }

  const results = RULES.map(r => ({ passed: r.test(password), label: r.label }));
  const passed = results.filter(r => r.passed).length;
  const missing = results.filter(r => !r.passed).map(r => r.label);

  const score = Math.min(4, passed) as 0 | 1 | 2 | 3 | 4;

  return {
    score,
    label: SCORE_LABELS[score],
    missing,
    isValid: passed === RULES.length,
  };
}

/** Zod-compatible refinement — use in .refine() calls */
export function validatePasswordComplexity(password: string): boolean {
  return getPasswordStrength(password).isValid;
}

export const PASSWORD_COMPLEXITY_MESSAGE =
  "Password must be at least 10 characters and include uppercase, lowercase, number, and special character (!@#$%^&* etc.)";
